import React, { useState } from 'react';
import { passwordlessAuthService } from '@turn-connect/shared/web';
import type { PendingPasswordReset } from '@turn-connect/shared';
import { Modal, Button } from '../dashboards/DashboardComponents';
import NewPasswordFields, { newPasswordReady } from './NewPasswordFields';

interface SetNewPasswordModalProps {
  reset: PendingPasswordReset;
  /** Called after the password is saved, or skipped when allowed. */
  onDone: () => void;
}

/**
 * Shown right after a "Forgot password?" sign-in (code or emailed link), or
 * after any code sign-in when the user's manager requires a new password.
 * The user is already signed in; this only chooses the password for next
 * time, so it can be skipped unless it is required.
 */
const SetNewPasswordModal: React.FC<SetNewPasswordModalProps> = ({ reset, onDone }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = newPasswordReady(password, confirm) && !saving;

  const save = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    const res = await passwordlessAuthService.completePasswordReset(reset.token, password);
    setSaving(false);
    if (res.success) {
      onDone();
      return;
    }
    const code = (res.errorBody as { code?: string } | undefined)?.code;
    if (code === 'INVALID_RESET_TOKEN') {
      setError('This reset has timed out. Sign out, then use Forgot password? again.');
    } else {
      setError(res.error || 'Could not save the password. Try again.');
    }
  };

  const skip = () => {
    if (!reset.required && !saving) onDone();
  };

  return (
    <Modal isOpen title="Choose a new password" onClose={skip} showCloseButton={!reset.required} width="440px">
      <form onSubmit={save} data-testid="set-new-password-modal">
        <p style={{ margin: '0 0 16px', fontSize: '13px', lineHeight: 1.5, color: 'var(--tc-text-secondary, #CCCCCC)' }}>
          {reset.required
            ? 'Your manager asked you to choose a new password before you continue.'
            : "You're signed in. Choose a new password for next time."}
        </p>

        <NewPasswordFields
          password={password}
          confirm={confirm}
          onPasswordChange={setPassword}
          onConfirmChange={setConfirm}
          disabled={saving}
          autoFocus
        />

        {error && (
          <div
            role="alert"
            style={{ marginTop: '12px', fontSize: '12px', color: 'var(--tc-status-error, #DC2626)' }}
            data-testid="set-new-password-error"
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          {!reset.required && (
            <Button variant="secondary" onClick={skip} disabled={saving}>
              Skip for now
            </Button>
          )}
          <Button type="submit" onClick={() => undefined} disabled={!canSave}>
            {saving ? 'Saving…' : 'Save password'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SetNewPasswordModal;
